import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, FileText, Settings2, GripVertical, Link2, FileOutput, Ruler, AlertTriangle } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  getGarmentTypeConfigs,
  getGarmentSpecificationFields,
  createGarmentSpecificationField,
  updateGarmentSpecificationField,
  deleteGarmentSpecificationField,
  bulkEditGarmentSpecificationFields,
  getBodyMeasurementFields,
  createBodyMeasurementField,
  updateBodyMeasurementField,
  deleteBodyMeasurementField,
  seedDefaultBodyMeasurementFields,
} from '@/lib/api';
import api from '@/lib/api';

// Available measurement fields for linking
const MEASUREMENT_FIELDS = [
  { key: 'neck_circumference', label: 'Neck Circumference' },
  { key: 'across_front', label: 'Across Front' },
  { key: 'chest_circumference', label: 'Chest Circumference' },
  { key: 'upper_waist_circumference', label: 'Upper Waist' },
  { key: 'belly_circumference', label: 'Belly' },
  { key: 'upper_seat_hip_circumference', label: 'Upper Seat/Hip' },
  { key: 'front_length_upper', label: 'Front Length' },
  { key: 'shoulder', label: 'Shoulder' },
  { key: 'across_back', label: 'Across Back' },
  { key: 'back_length_upper', label: 'Back Length' },
  { key: 'biceps_circumference', label: 'Biceps' },
  { key: 'elbow_circumference', label: 'Elbow' },
  { key: 'sleeve_length', label: 'Sleeve Length' },
  { key: 'left_wrist_circumference', label: 'Left Wrist' },
  { key: 'right_wrist_circumference', label: 'Right Wrist' },
  { key: 'waistcoat_front_neck_depth', label: 'Waistcoat Front Neck Depth' },
  { key: 'waistcoat_front_length', label: 'Waistcoat Front Length' },
  { key: 'waistcoat_back_length', label: 'Waistcoat Back Length' },
  { key: 'lower_waist_circumference', label: 'Lower Waist' },
  { key: 'lower_seat_hip_circumference', label: 'Lower Seat/Hip' },
  { key: 'front_rise', label: 'Front Rise' },
  { key: 'u_measurement', label: 'U-Measure' },
  { key: 'thigh_circumference', label: 'Thigh' },
  { key: 'knee_circumference', label: 'Knee' },
  { key: 'outseam', label: 'Outseam' },
];

const GARMENT_COLORS = {
  jacket: 'bg-blue-500',
  trouser: 'bg-green-500',
  shirt: 'bg-purple-500',
  waistcoat: 'bg-amber-500',
  overcoat: 'bg-slate-500',
};

// Sortable Row Component
const SortableRow = ({ field, onEdit, onDelete, isSelected, onToggleSelect }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.field_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: isDragging ? 'var(--muted)' : undefined,
  };

  return (
    <tr ref={setNodeRef} style={style} className="border-b">
      <td className="p-4 w-10">
        <input
          type="checkbox"
          checked={Boolean(isSelected)}
          onChange={() => onToggleSelect(field.field_id)}
          onClick={(e) => e.stopPropagation()}
          data-testid={`garment-spec-field-select-${field.field_id}`}
        />
      </td>
      <td className="p-4 w-12">
        <button
          type="button"
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded"
          {...attributes}
          {...listeners}
          data-testid={`garment-spec-field-drag-${field.field_id}`}
        >
          <GripVertical className="w-4 h-4 text-muted-foreground" />
        </button>
      </td>
      <td className="p-4 text-muted-foreground text-sm">
        {field.display_order}
      </td>
      <td className="p-4 font-medium">{field.field_label}</td>
      <td className="p-4">
        <code className="text-xs bg-muted px-2 py-1 rounded">{field.field_key}</code>
      </td>
      <td className="p-4">
        <Badge variant={field.field_type === 'select' ? 'secondary' : 'outline'}>
          {field.field_type}
        </Badge>
      </td>
      <td className="p-4">
        {field.linked_measurement_field ? (
          <div className="flex items-center gap-1 text-sm text-amber-500">
            <Link2 className="w-3 h-3" />
            {MEASUREMENT_FIELDS.find(m => m.key === field.linked_measurement_field)?.label || field.linked_measurement_field}
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      <td className="p-4">
        <div className="flex items-center gap-2">
          {field.include_in_pdf && (
            <Badge variant="outline" className="text-xs">
              <FileOutput className="w-3 h-3 mr-1" />
              PDF
            </Badge>
          )}
          {field.unit_display && (
            <Badge variant="outline" className="text-xs">
              <Ruler className="w-3 h-3 mr-1" />
              Unit
            </Badge>
          )}
        </div>
      </td>
      <td className="p-4 text-right">
        <div className="flex items-center justify-end gap-1">
          <Button variant="ghost" size="icon" onClick={() => onEdit(field)} data-testid={`garment-spec-field-edit-${field.field_id}`}>
            <Edit2 className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => onDelete(field.field_id)} data-testid={`garment-spec-field-delete-${field.field_id}`}>
            <Trash2 className="w-4 h-4 text-destructive" />
          </Button>
        </div>
      </td>
    </tr>
  );
};

const SortableBodyRow = ({ field, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.field_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    position: 'relative',
    zIndex: isDragging ? 10 : 1,
  };

  return (
    <tr ref={setNodeRef} style={style} className="border-b last:border-b-0 bg-background" data-testid={`body-sortable-row-${field.field_key}`}>
      <td className="p-3">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground transition-colors"
          data-testid={`body-field-drag-handle-${field.field_key}`}
        >
          <GripVertical className="w-4 h-4" />
        </button>
      </td>
      <td className="p-3 text-sm text-muted-foreground">{field.display_order}</td>
      <td className="p-3 font-medium">{field.field_label}</td>
      <td className="p-3"><code className="text-xs bg-muted px-2 py-1 rounded">{field.field_key}</code></td>
      <td className="p-3"><Badge variant={field.field_type === 'text' ? 'secondary' : 'outline'}>{field.field_type}</Badge></td>
      <td className="p-3"><Badge variant={field.is_active ? 'default' : 'outline'}>{field.is_active ? 'Active' : 'Hidden'}</Badge></td>
      <td className="p-3 text-right">
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="icon" onClick={() => onEdit(field)} data-testid={`edit-body-field-${field.field_key}`}>
            <Edit2 className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => onDelete(field.field_id)} data-testid={`delete-body-field-${field.field_key}`}>
            <Trash2 className="w-4 h-4 text-destructive" />
          </Button>
        </div>
      </td>
    </tr>
  );
};

const PatternSpecificationsPage = () => {
  const [activeTab, setActiveTab] = useState('body');

  // Body measurement fields
  const [bodyFields, setBodyFields] = useState([]);
  const [bodyFieldsLoading, setBodyFieldsLoading] = useState(false);
  const [bodyFieldDialogOpen, setBodyFieldDialogOpen] = useState(false);
  const [deleteBodyDialogOpen, setDeleteBodyDialogOpen] = useState(false);
  const [editingBodyField, setEditingBodyField] = useState(null);
  const [deletingBodyFieldId, setDeletingBodyFieldId] = useState(null);
  const [bodyFieldSaving, setBodyFieldSaving] = useState(false);
  const [bodyReordering, setBodyReordering] = useState(false);
  const [bodyFieldForm, setBodyFieldForm] = useState({
    field_label: '',
    field_key: '',
    field_type: 'number',
    display_order: 0,
    is_active: true,
  });

  const [configs, setConfigs] = useState([]);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fieldsLoading, setFieldsLoading] = useState(false);
  
  // Dialog states
  const [fieldDialogOpen, setFieldDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [deletingFieldId, setDeletingFieldId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [selectedFieldIds, setSelectedFieldIds] = useState([]);
  const [bulkEditDialogOpen, setBulkEditDialogOpen] = useState(false);
  const [bulkEditConfirmOpen, setBulkEditConfirmOpen] = useState(false);
  const [bulkEditing, setBulkEditing] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkEditForm, setBulkEditForm] = useState({
    field_type: 'no_change',
    select_options: '',
    unit_display: 'no_change',
  });
  
  // Form data
  const [fieldForm, setFieldForm] = useState({
    field_label: '',
    field_key: '',
    field_type: 'text',
    select_options: '',
    linked_measurement_field: '',
    display_order: 0,
    include_in_pdf: true,
    unit_display: false,
  });

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchConfigs = useCallback(async () => {
    try {
      const response = await getGarmentTypeConfigs();
      setConfigs(response.data);
      
      // Auto-select first config
      if (response.data.length > 0 && !selectedConfig) {
        setSelectedConfig(response.data[0]);
      }
    } catch (error) {
      toast.error('Failed to fetch garment configurations');
    } finally {
      setLoading(false);
    }
  }, [selectedConfig]);

  const fetchBodyFields = useCallback(async () => {
    setBodyFieldsLoading(true);
    try {
      const response = await getBodyMeasurementFields();
      const sortedFields = (response.data || []).sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
      setBodyFields(sortedFields);
    } catch (error) {
      toast.error('Failed to fetch body measurement fields');
    } finally {
      setBodyFieldsLoading(false);
    }
  }, []);

  const fetchFields = useCallback(async () => {
    if (!selectedConfig) return;
    
    setFieldsLoading(true);
    try {
      const response = await getGarmentSpecificationFields(selectedConfig.config_id);
      // Sort by display_order
      const sortedFields = response.data.sort((a, b) => a.display_order - b.display_order);
      setFields(sortedFields);
    } catch (error) {
      toast.error('Failed to fetch specification fields');
    } finally {
      setFieldsLoading(false);
    }
  }, [selectedConfig]);

  useEffect(() => {
    fetchConfigs();
    fetchBodyFields();
  }, [fetchConfigs, fetchBodyFields]);

  useEffect(() => {
    if (selectedConfig) {
      fetchFields();
    }
  }, [selectedConfig, fetchFields]);

  useEffect(() => {
    setSelectedFieldIds((prev) => prev.filter((fieldId) => fields.some((field) => field.field_id === fieldId)));
  }, [fields]);

  const handleSeedDefaults = async () => {
    try {
      const response = await api.post('/garment-specification-fields/seed-defaults');
      toast.success(response.data.message);
      fetchFields();
    } catch (error) {
      toast.error('Failed to seed default fields');
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = fields.findIndex(f => f.field_id === active.id);
    const newIndex = fields.findIndex(f => f.field_id === over.id);
    
    // Optimistically update UI
    const newFields = arrayMove(fields, oldIndex, newIndex);
    
    // Update display_order for all fields
    const updatedFields = newFields.map((field, index) => ({
      ...field,
      display_order: index + 1,
    }));
    
    setFields(updatedFields);
    setReordering(true);

    // Save new order to backend
    try {
      // Update each field's display_order
      await Promise.all(
        updatedFields.map((field) =>
          updateGarmentSpecificationField(field.field_id, {
            garment_type_config_id: selectedConfig.config_id,
            field_label: field.field_label,
            field_key: field.field_key,
            field_type: field.field_type,
            select_options: field.select_options || [],
            linked_measurement_field: field.linked_measurement_field,
            display_order: field.display_order,
            include_in_pdf: field.include_in_pdf,
            unit_display: field.unit_display,
          })
        )
      );
      toast.success('Field order updated');
    } catch (error) {
      toast.error('Failed to save field order');
      // Revert on error
      fetchFields();
    } finally {
      setReordering(false);
    }
  };

  const handleOpenNewField = () => {
    setEditingField(null);
    setFieldForm({
      field_label: '',
      field_key: '',
      field_type: 'text',
      select_options: '',
      linked_measurement_field: '',
      display_order: fields.length + 1,
      include_in_pdf: true,
      unit_display: false,
    });
    setFieldDialogOpen(true);
  };

  const handleEditField = (field) => {
    setEditingField(field);
    setFieldForm({
      field_label: field.field_label,
      field_key: field.field_key,
      field_type: field.field_type,
      select_options: (field.select_options || []).join(', '),
      linked_measurement_field: field.linked_measurement_field || '',
      display_order: field.display_order,
      include_in_pdf: field.include_in_pdf,
      unit_display: field.unit_display,
    });
    setFieldDialogOpen(true);
  };

  const handleDeleteField = (fieldId) => {
    setDeletingFieldId(fieldId);
    setDeleteDialogOpen(true);
  };

  const toggleFieldSelection = (fieldId) => {
    setSelectedFieldIds((prev) => (
      prev.includes(fieldId)
        ? prev.filter((id) => id !== fieldId)
        : [...prev, fieldId]
    ));
  };

  const toggleSelectAllFields = () => {
    if (selectedFieldIds.length === fields.length) {
      setSelectedFieldIds([]);
      return;
    }
    setSelectedFieldIds(fields.map((field) => field.field_id));
  };

  const confirmDelete = async () => {
    try {
      await deleteGarmentSpecificationField(deletingFieldId);
      toast.success('Field deleted');
      setFields((prev) => prev.filter((field) => field.field_id !== deletingFieldId));
      setSelectedFieldIds((prev) => prev.filter((fieldId) => fieldId !== deletingFieldId));
    } catch (error) {
      toast.error('Failed to delete field');
    } finally {
      setDeleteDialogOpen(false);
      setDeletingFieldId(null);
    }
  };

  const confirmBulkDelete = async () => {
    if (selectedFieldIds.length === 0) return;
    try {
      setBulkDeleting(true);
      await Promise.all(selectedFieldIds.map((fieldId) => deleteGarmentSpecificationField(fieldId)));
      setFields((prev) => prev.filter((field) => !selectedFieldIds.includes(field.field_id)));
      toast.success(`${selectedFieldIds.length} field${selectedFieldIds.length > 1 ? 's' : ''} deleted`);
      setSelectedFieldIds([]);
      setBulkDeleteDialogOpen(false);
    } catch (error) {
      toast.error('Failed to delete selected fields');
    } finally {
      setBulkDeleting(false);
    }
  };

  const resetBulkEditForm = () => {
    setBulkEditForm({
      field_type: 'no_change',
      select_options: '',
      unit_display: 'no_change',
    });
  };

  const handleOpenBulkEdit = () => {
    resetBulkEditForm();
    setBulkEditDialogOpen(true);
  };

  const handleRequestBulkEditConfirm = () => {
    if (selectedFieldIds.length === 0) {
      toast.error('Select at least one field');
      return;
    }

    if (bulkEditForm.field_type === 'no_change' && bulkEditForm.unit_display === 'no_change') {
      toast.error('Choose at least one bulk change');
      return;
    }

    if (bulkEditForm.field_type === 'select' && !bulkEditForm.select_options.trim()) {
      toast.error('Provide dropdown options when changing field type to Dropdown');
      return;
    }

    setBulkEditConfirmOpen(true);
  };

  const confirmBulkEdit = async () => {
    if (selectedFieldIds.length === 0) return;

    const payload = {
      field_ids: selectedFieldIds,
    };

    if (bulkEditForm.field_type !== 'no_change') {
      payload.field_type = bulkEditForm.field_type;
      if (bulkEditForm.field_type === 'select') {
        payload.select_options = bulkEditForm.select_options
          .split(',')
          .map((option) => option.trim())
          .filter(Boolean);
      }
    }

    if (bulkEditForm.unit_display !== 'no_change') {
      payload.unit_display = bulkEditForm.unit_display === 'show';
    }

    try {
      setBulkEditing(true);
      const response = await bulkEditGarmentSpecificationFields(payload);
      toast.success(`${response.data.updated_count || selectedFieldIds.length} field${selectedFieldIds.length > 1 ? 's' : ''} updated`);
      setBulkEditConfirmOpen(false);
      setBulkEditDialogOpen(false);
      setSelectedFieldIds([]);
      resetBulkEditForm();
      fetchFields();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to update selected fields');
    } finally {
      setBulkEditing(false);
    }
  };

  const handleSaveField = async () => {
    if (!fieldForm.field_label || !fieldForm.field_key) {
      toast.error('Field label and key are required');
      return;
    }
    
    setSaving(true);
    try {
      const data = {
        garment_type_config_id: selectedConfig.config_id,
        field_label: fieldForm.field_label,
        field_key: fieldForm.field_key.toLowerCase().replace(/\s+/g, '_'),
        field_type: fieldForm.field_type,
        select_options: fieldForm.field_type === 'select' 
          ? fieldForm.select_options.split(',').map(o => o.trim()).filter(o => o)
          : [],
        linked_measurement_field: fieldForm.linked_measurement_field || null,
        display_order: parseInt(fieldForm.display_order) || fields.length + 1,
        include_in_pdf: fieldForm.include_in_pdf,
        unit_display: fieldForm.unit_display,
      };
      
      if (editingField) {
        await updateGarmentSpecificationField(editingField.field_id, data);
        toast.success('Field updated');
      } else {
        await createGarmentSpecificationField(data);
        toast.success('Field created');
      }
      
      setFieldDialogOpen(false);
      fetchFields();
    } catch (error) {
      toast.error('Failed to save field');
    } finally {
      setSaving(false);
    }
  };

  const generateFieldKey = (label) => {
    const prefix = selectedConfig?.garment_type?.substring(0, 2).toLowerCase() || 'xx';
    return `${prefix}_${label.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`;
  };

  const generateBodyFieldKey = (label) => {
    return label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
  };

  const handleSeedBodyDefaults = async () => {
    try {
      const response = await seedDefaultBodyMeasurementFields();
      toast.success(response.data.message);
      fetchBodyFields();
    } catch (error) {
      toast.error('Failed to seed default body measurement fields');
    }
  };

  const handleOpenNewBodyField = () => {
    setEditingBodyField(null);
    setBodyFieldForm({
      field_label: '',
      field_key: '',
      field_type: 'number',
      display_order: bodyFields.length + 1,
      is_active: true,
    });
    setBodyFieldDialogOpen(true);
  };

  const handleEditBodyField = (field) => {
    setEditingBodyField(field);
    setBodyFieldForm({
      field_label: field.field_label,
      field_key: field.field_key,
      field_type: field.field_type || 'number',
      display_order: field.display_order || 0,
      is_active: Boolean(field.is_active),
    });
    setBodyFieldDialogOpen(true);
  };

  const handleDeleteBodyField = (fieldId) => {
    setDeletingBodyFieldId(fieldId);
    setDeleteBodyDialogOpen(true);
  };

  const confirmDeleteBodyField = async () => {
    if (!deletingBodyFieldId) return;
    try {
      await deleteBodyMeasurementField(deletingBodyFieldId);
      toast.success('Body measurement field deleted');
      fetchBodyFields();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to delete body field');
    } finally {
      setDeleteBodyDialogOpen(false);
      setDeletingBodyFieldId(null);
    }
  };

  const handleSaveBodyField = async () => {
    if (!bodyFieldForm.field_label || !bodyFieldForm.field_key) {
      toast.error('Field label and key are required');
      return;
    }

    setBodyFieldSaving(true);
    try {
      const payload = {
        field_label: bodyFieldForm.field_label,
        field_key: bodyFieldForm.field_key,
        field_type: bodyFieldForm.field_type,
        display_order: parseInt(bodyFieldForm.display_order, 10) || bodyFields.length + 1,
        is_active: Boolean(bodyFieldForm.is_active),
      };

      if (editingBodyField) {
        await updateBodyMeasurementField(editingBodyField.field_id, payload);
        toast.success('Body measurement field updated');
      } else {
        await createBodyMeasurementField(payload);
        toast.success('Body measurement field created');
      }

      setBodyFieldDialogOpen(false);
      fetchBodyFields();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to save body measurement field');
    } finally {
      setBodyFieldSaving(false);
    }
  };

  const buildBodyFieldPayload = (field) => ({
    field_label: field.field_label,
    field_key: field.field_key,
    field_type: field.field_type,
    display_order: field.display_order,
    is_active: Boolean(field.is_active),
  });

  const handleBodyDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = bodyFields.findIndex((item) => item.field_id === active.id);
    const newIndex = bodyFields.findIndex((item) => item.field_id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const reordered = arrayMove(bodyFields, oldIndex, newIndex).map((field, index) => ({
      ...field,
      display_order: index + 1,
    }));

    setBodyFields(reordered);
    setBodyReordering(true);
    try {
      await Promise.all(
        reordered.map((field) => updateBodyMeasurementField(field.field_id, buildBodyFieldPayload(field)))
      );
      toast.success('Body field order updated');
    } catch (error) {
      toast.error('Failed to save body field order');
      fetchBodyFields();
    } finally {
      setBodyReordering(false);
    }
  };

  if (loading) {
    return (
      <div className="p-3 space-y-6">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="p-3 space-y-6">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            Pattern Specifications
          </h1>
          <p className="text-muted-foreground">Configure body measurement fields and garment specification fields.</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4" data-testid="pattern-specifications-tabs">
        <TabsList data-testid="pattern-specifications-tabs-list">
          <TabsTrigger value="body" data-testid="pattern-specifications-tab-body">Body Measurements</TabsTrigger>
          <TabsTrigger value="garment" data-testid="pattern-specifications-tab-garment">Garment Specifications</TabsTrigger>
        </TabsList>

        <TabsContent value="body" className="space-y-4" data-testid="pattern-specifications-body-tab-content">
          <Card className="border-amber-300/60 bg-amber-50/50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3 text-amber-900">
                <AlertTriangle className="w-4 h-4 mt-0.5" />
                <p className="text-sm" data-testid="body-measurement-field-warning-text">
                  Field key and type are editable. Changing type from number to text may impact calculation-driven workflows.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-white/10 bg-card" data-testid="body-measurement-fields-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Ruler className="w-5 h-5 text-amber-500" />
                  Body Measurement Field Definitions
                </CardTitle>
                <CardDescription>
                  {bodyFields.length} field{bodyFields.length !== 1 ? 's' : ''} defined
                  {bodyReordering && <span className="ml-2 text-amber-500">• Saving order...</span>}
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleSeedBodyDefaults} data-testid="seed-default-body-fields-button">
                  <Settings2 className="w-4 h-4 mr-2" />
                  Seed Defaults
                </Button>
                <Button onClick={handleOpenNewBodyField} data-testid="add-body-field-button">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Body Field
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {bodyFieldsLoading ? (
                <Skeleton className="h-64 w-full" />
              ) : bodyFields.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground" data-testid="body-fields-empty-state">
                  <Ruler className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No body measurement fields defined</p>
                  <div className="flex gap-2 justify-center mt-4">
                    <Button variant="outline" onClick={handleOpenNewBodyField} data-testid="add-first-body-field-button">
                      <Plus className="w-4 h-4 mr-2" />
                      Add First Field
                    </Button>
                    <Button variant="outline" onClick={handleSeedBodyDefaults} data-testid="seed-default-body-fields-empty-button">
                      <Settings2 className="w-4 h-4 mr-2" />
                      Seed Defaults
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full" data-testid="body-measurement-fields-table">
                    <thead className="bg-muted/50">
                      <tr className="border-b">
                        <th className="p-3 text-left w-12"></th>
                        <th className="p-3 text-left text-sm font-medium text-muted-foreground w-16">#</th>
                        <th className="p-3 text-left text-sm font-medium">Field Label</th>
                        <th className="p-3 text-left text-sm font-medium">Field Key</th>
                        <th className="p-3 text-left text-sm font-medium">Type</th>
                        <th className="p-3 text-left text-sm font-medium">Status</th>
                        <th className="p-3 text-right text-sm font-medium">Actions</th>
                      </tr>
                    </thead>
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleBodyDragEnd}>
                      <SortableContext items={bodyFields.map((field) => field.field_id)} strategy={verticalListSortingStrategy}>
                        <tbody>
                          {bodyFields.map((field) => (
                            <SortableBodyRow
                              key={field.field_id}
                              field={field}
                              onEdit={handleEditBodyField}
                              onDelete={handleDeleteBodyField}
                            />
                          ))}
                        </tbody>
                      </SortableContext>
                    </DndContext>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="garment" className="space-y-4" data-testid="pattern-specifications-garment-tab-content">
          {/* Garment Type Selection */}
          <Card className="border-white/10 bg-card">
            <CardHeader>
              <CardTitle>Select Garment Type</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {configs.map(config => {
                  const colorClass = GARMENT_COLORS[config.garment_type] || 'bg-slate-500';
                  const isSelected = selectedConfig?.config_id === config.config_id;

                  return (
                    <Button
                      key={config.config_id}
                      variant={isSelected ? 'default' : 'outline'}
                      className={isSelected ? colorClass : ''}
                      onClick={() => setSelectedConfig(config)}
                    >
                      {config.title}
                    </Button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {selectedConfig && (
            <Card className="border-white/10 bg-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-amber-500" />
                    Manage Fields: {selectedConfig.title}
                  </CardTitle>
                  <CardDescription>
                    {fields.length} specification field{fields.length !== 1 ? 's' : ''} defined
                    {reordering && <span className="ml-2 text-amber-500">• Saving order...</span>}
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={handleSeedDefaults} data-testid="seed-default-garment-fields-button">
                    <Settings2 className="w-4 h-4 mr-2" />
                    Seed Defaults
                  </Button>
                  <Button onClick={handleOpenNewField} data-testid="add-garment-field-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Field
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {fieldsLoading ? (
                  <Skeleton className="h-64 w-full" />
                ) : fields.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No specification fields defined</p>
                    <div className="flex gap-2 justify-center mt-4">
                      <Button variant="outline" onClick={handleOpenNewField}>
                        <Plus className="w-4 h-4 mr-2" />
                        Add First Field
                      </Button>
                      <Button variant="outline" onClick={handleSeedDefaults}>
                        <Settings2 className="w-4 h-4 mr-2" />
                        Seed Defaults
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-3 py-2" data-testid="garment-spec-bulk-action-bar">
                      <div className="text-sm text-amber-900" data-testid="garment-spec-selected-count">
                        {selectedFieldIds.length} selected
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleOpenBulkEdit}
                          disabled={selectedFieldIds.length === 0}
                          data-testid="garment-spec-bulk-edit-button"
                        >
                          Bulk Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedFieldIds([])}
                          disabled={selectedFieldIds.length === 0}
                          data-testid="garment-spec-clear-selection-button"
                        >
                          Clear Selection
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => setBulkDeleteDialogOpen(true)}
                          disabled={selectedFieldIds.length === 0}
                          data-testid="garment-spec-bulk-delete-button"
                        >
                          Delete Selected
                        </Button>
                      </div>
                    </div>

                    <div className="border rounded-lg overflow-hidden">
                    <table className="w-full" data-testid="garment-spec-fields-table">
                      <thead className="bg-muted/50">
                        <tr className="border-b">
                          <th className="p-4 text-left w-10">
                            <input
                              type="checkbox"
                              checked={fields.length > 0 && selectedFieldIds.length === fields.length}
                              onChange={toggleSelectAllFields}
                              data-testid="garment-spec-select-all-checkbox"
                            />
                          </th>
                          <th className="p-4 text-left w-12"></th>
                          <th className="p-4 text-left text-sm font-medium text-muted-foreground w-16">#</th>
                          <th className="p-4 text-left text-sm font-medium">Field Label</th>
                          <th className="p-4 text-left text-sm font-medium">Field Key</th>
                          <th className="p-4 text-left text-sm font-medium">Type</th>
                          <th className="p-4 text-left text-sm font-medium">Linked Measurement</th>
                          <th className="p-4 text-left text-sm font-medium">Options</th>
                          <th className="p-4 text-right text-sm font-medium">Actions</th>
                        </tr>
                      </thead>
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                      >
                        <SortableContext
                          items={fields.map(f => f.field_id)}
                          strategy={verticalListSortingStrategy}
                        >
                          <tbody>
                            {fields.map((field) => (
                              <SortableRow
                                key={field.field_id}
                                field={field}
                                onEdit={handleEditField}
                                onDelete={handleDeleteField}
                                isSelected={selectedFieldIds.includes(field.field_id)}
                                onToggleSelect={toggleFieldSelection}
                              />
                            ))}
                          </tbody>
                        </SortableContext>
                      </DndContext>
                    </table>
                  </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Body Field Dialog */}
      <Dialog open={bodyFieldDialogOpen} onOpenChange={setBodyFieldDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="body-field-dialog">
          <DialogHeader>
            <DialogTitle>{editingBodyField ? 'Edit Body Measurement Field' : 'Add Body Measurement Field'}</DialogTitle>
            <DialogDescription>
              Configure label, key, type, and display order for body measurements.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 rounded-md border border-amber-300 bg-amber-50/60 text-amber-900 text-xs" data-testid="body-field-edit-warning">
              Warning: field key and type are editable. Changing them can affect existing references and calculations.
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Field Label *</Label>
                <Input
                  value={bodyFieldForm.field_label}
                  onChange={(e) => setBodyFieldForm((prev) => ({
                    ...prev,
                    field_label: e.target.value,
                    field_key: editingBodyField ? prev.field_key : generateBodyFieldKey(e.target.value),
                  }))}
                  placeholder="e.g., Left Wrist"
                  data-testid="body-field-label-input"
                />
              </div>
              <div>
                <Label>Field Key *</Label>
                <Input
                  value={bodyFieldForm.field_key}
                  onChange={(e) => setBodyFieldForm((prev) => ({ ...prev, field_key: e.target.value }))}
                  placeholder="left_wrist_circumference"
                  data-testid="body-field-key-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Field Type</Label>
                <Select
                  value={bodyFieldForm.field_type}
                  onValueChange={(value) => setBodyFieldForm((prev) => ({ ...prev, field_type: value }))}
                >
                  <SelectTrigger data-testid="body-field-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="number">Number</SelectItem>
                    <SelectItem value="text">Text</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Display Order</Label>
                <Input
                  type="number"
                  value={bodyFieldForm.display_order}
                  onChange={(e) => setBodyFieldForm((prev) => ({ ...prev, display_order: e.target.value }))}
                  data-testid="body-field-display-order-input"
                />
              </div>
            </div>

            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div>
                <Label>Active</Label>
                <p className="text-xs text-muted-foreground">Inactive fields are hidden in Fitting Studio form.</p>
              </div>
              <Switch
                checked={bodyFieldForm.is_active}
                onCheckedChange={(checked) => setBodyFieldForm((prev) => ({ ...prev, is_active: checked }))}
                data-testid="body-field-active-switch"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBodyFieldDialogOpen(false)} data-testid="body-field-dialog-cancel-button">Cancel</Button>
            <Button onClick={handleSaveBodyField} disabled={bodyFieldSaving} data-testid="body-field-dialog-save-button">
              {bodyFieldSaving ? 'Saving...' : editingBodyField ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Body Delete Confirmation */}
      <AlertDialog open={deleteBodyDialogOpen} onOpenChange={setDeleteBodyDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Body Measurement Field?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the field definition from Body Measurements configuration.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteBodyField} className="bg-destructive hover:bg-destructive/90" data-testid="confirm-delete-body-field-button">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={bulkEditDialogOpen} onOpenChange={setBulkEditDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="garment-spec-bulk-edit-dialog">
          <DialogHeader>
            <DialogTitle data-testid="garment-spec-bulk-edit-title">Bulk Edit Specification Fields</DialogTitle>
            <DialogDescription data-testid="garment-spec-bulk-edit-description">
              Apply the same settings to {selectedFieldIds.length} selected field{selectedFieldIds.length === 1 ? '' : 's'}.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-lg border border-amber-300 bg-amber-50/70 p-3 text-sm text-amber-900" data-testid="garment-spec-bulk-edit-warning">
              <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <div>
                  Bulk edit will overwrite the selected fields after confirmation. Changing field type may clear incompatible dropdown options, and non-number fields will not show measurement units.
                </div>
              </div>
            </div>

            <div>
              <Label data-testid="garment-spec-bulk-field-type-label">Field Type</Label>
              <Select
                value={bulkEditForm.field_type}
                onValueChange={(value) => setBulkEditForm((prev) => ({
                  ...prev,
                  field_type: value,
                  unit_display: value !== 'number' && value !== 'no_change' ? 'hide' : prev.unit_display,
                }))}
              >
                <SelectTrigger data-testid="garment-spec-bulk-field-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="no_change">No change</SelectItem>
                  <SelectItem value="text">Text</SelectItem>
                  <SelectItem value="number">Number</SelectItem>
                  <SelectItem value="select">Dropdown</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {bulkEditForm.field_type === 'select' && (
              <div>
                <Label data-testid="garment-spec-bulk-select-options-label">Dropdown Options</Label>
                <Input
                  value={bulkEditForm.select_options}
                  onChange={(e) => setBulkEditForm((prev) => ({ ...prev, select_options: e.target.value }))}
                  placeholder="Option 1, Option 2, Option 3"
                  data-testid="garment-spec-bulk-select-options-input"
                />
                <p className="mt-1 text-xs text-muted-foreground" data-testid="garment-spec-bulk-select-options-help">
                  Required when changing selected fields to Dropdown.
                </p>
              </div>
            )}

            <div>
              <Label data-testid="garment-spec-bulk-unit-display-label">Display Measurement Unit</Label>
              <Select
                value={bulkEditForm.unit_display}
                onValueChange={(value) => setBulkEditForm((prev) => ({ ...prev, unit_display: value }))}
              >
                <SelectTrigger data-testid="garment-spec-bulk-unit-display-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="no_change">No change</SelectItem>
                  <SelectItem value="show">Show unit</SelectItem>
                  <SelectItem value="hide">Hide unit</SelectItem>
                </SelectContent>
              </Select>
              <p className="mt-1 text-xs text-muted-foreground" data-testid="garment-spec-bulk-unit-display-help">
                This applies to number fields. If the final type is not Number, unit display will be turned off.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setBulkEditDialogOpen(false);
                resetBulkEditForm();
              }}
              data-testid="garment-spec-bulk-edit-cancel-button"
            >
              Cancel
            </Button>
            <Button onClick={handleRequestBulkEditConfirm} data-testid="garment-spec-bulk-edit-review-button">
              Review Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={bulkEditConfirmOpen} onOpenChange={setBulkEditConfirmOpen}>
        <AlertDialogContent data-testid="garment-spec-bulk-edit-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="garment-spec-bulk-edit-confirm-title">Apply bulk changes?</AlertDialogTitle>
            <AlertDialogDescription data-testid="garment-spec-bulk-edit-confirm-description">
              You are about to overwrite {selectedFieldIds.length} selected field{selectedFieldIds.length === 1 ? '' : 's'} from {selectedConfig?.title || 'this garment type'}.
              {bulkEditForm.field_type !== 'no_change' ? ` New field type: ${bulkEditForm.field_type}.` : ''}
              {bulkEditForm.unit_display !== 'no_change' ? ` Display Measurement Unit: ${bulkEditForm.unit_display === 'show' ? 'Show' : 'Hide'}.` : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="garment-spec-bulk-edit-confirm-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmBulkEdit}
              disabled={bulkEditing}
              data-testid="garment-spec-bulk-edit-confirm-apply-button"
            >
              {bulkEditing ? 'Applying...' : 'Apply Changes'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Garment Bulk Delete Confirmation */}
      <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete selected specification fields?</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to delete {selectedFieldIds.length} field{selectedFieldIds.length === 1 ? '' : 's'} from {selectedConfig?.title || 'this garment type'}.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="garment-spec-bulk-delete-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmBulkDelete}
              className="bg-destructive hover:bg-destructive/90"
              disabled={bulkDeleting}
              data-testid="garment-spec-bulk-delete-confirm-button"
            >
              {bulkDeleting ? 'Deleting...' : 'Delete Selected'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Field Dialog */}
      <Dialog open={fieldDialogOpen} onOpenChange={setFieldDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingField ? 'Edit Specification Field' : 'Add Specification Field'}
            </DialogTitle>
            <DialogDescription>
              Configure how this field appears in pattern specifications
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Field Label *</Label>
                <Input
                  placeholder="e.g., Chest (Jacket)"
                  value={fieldForm.field_label}
                  onChange={(e) => {
                    setFieldForm(prev => ({
                      ...prev,
                      field_label: e.target.value,
                      field_key: editingField ? prev.field_key : generateFieldKey(e.target.value),
                    }));
                  }}
                />
              </div>
              <div>
                <Label>Field Key *</Label>
                <Input
                  placeholder="e.g., jk_chest"
                  value={fieldForm.field_key}
                  onChange={(e) => setFieldForm(prev => ({ ...prev, field_key: e.target.value }))}
                />
                <p className="text-xs text-muted-foreground mt-1">Used internally, no spaces</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Field Type</Label>
                <Select
                  value={fieldForm.field_type}
                  onValueChange={(value) => setFieldForm(prev => ({ ...prev, field_type: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="text">Text</SelectItem>
                    <SelectItem value="number">Number</SelectItem>
                    <SelectItem value="select">Select (Dropdown)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Display Order</Label>
                <Input
                  type="number"
                  value={fieldForm.display_order}
                  onChange={(e) => setFieldForm(prev => ({ ...prev, display_order: e.target.value }))}
                />
              </div>
            </div>

            {fieldForm.field_type === 'select' && (
              <div>
                <Label>Select Options</Label>
                <Input
                  placeholder="Option 1, Option 2, Option 3"
                  value={fieldForm.select_options}
                  onChange={(e) => setFieldForm(prev => ({ ...prev, select_options: e.target.value }))}
                />
                <p className="text-xs text-muted-foreground mt-1">Comma-separated list of options</p>
              </div>
            )}

            <div>
              <Label>Linked Body Measurement</Label>
              <Select
                value={fieldForm.linked_measurement_field || 'none'}
                onValueChange={(value) => setFieldForm(prev => ({ 
                  ...prev, 
                  linked_measurement_field: value === 'none' ? '' : value 
                }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select measurement to link..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Link</SelectItem>
                  {MEASUREMENT_FIELDS.map(m => (
                    <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Auto-populate from customer measurements when creating patterns
              </p>
            </div>

            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
              <div>
                <Label>Include in Order Sheet PDF</Label>
                <p className="text-xs text-muted-foreground">Show this field on generated PDFs</p>
              </div>
              <Switch
                checked={fieldForm.include_in_pdf}
                onCheckedChange={(checked) => setFieldForm(prev => ({ ...prev, include_in_pdf: checked }))}
              />
            </div>

            {fieldForm.field_type === 'number' && (
              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                <div>
                  <Label>Display Measurement Unit</Label>
                  <p className="text-xs text-muted-foreground">Append &quot;in&quot; or &quot;cm&quot; to values</p>
                </div>
                <Switch
                  checked={fieldForm.unit_display}
                  onCheckedChange={(checked) => setFieldForm(prev => ({ ...prev, unit_display: checked }))}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFieldDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveField} disabled={saving}>
              {saving ? 'Saving...' : (editingField ? 'Update' : 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Specification Field?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete this specification field.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PatternSpecificationsPage;
